/**
 * A ponte de sessão entre `yardsuite.com.br` (raiz) e `{cliente}.yardsuite.com.br`
 * (a porta do cliente na VPS nova, com vhost curinga).
 *
 * POR QUE ISTO EXISTE
 *
 *   São origens diferentes pro navegador, e o Supabase guarda a sessão no
 *   `localStorage` da origem em que você logou. Um `<Navigate>` comum não
 *   troca de host; um `window.location.href` cru troca de host mas chega do
 *   outro lado deslogado, porque o `localStorage` de lá está vazio.
 *
 *   A saída é levar o par de tokens junto, na FRAGMENT da URL (depois do
 *   `#`). Fragment nunca sai do navegador - não vai em log de servidor, não
 *   vai em `Referer`, não é enviado em requisição nenhuma. É visível só
 *   nesta aba, só neste instante, e este componente apaga da barra de
 *   endereço e do histórico ANTES de qualquer outra coisa acontecer.
 *
 *   Duas metades: `EnviarParaSubdominio` (chamada da raiz, ver
 *   `RaizDoPainel` em `app/Router.tsx`) monta a URL e troca de host;
 *   `ReceberDeSubdominio` (a rota `/_sso`, global, ver `SEM_PREFIXO` em
 *   `core/rotas.tsx`) lê o fragment do outro lado e grava a sessão.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../core/supabase';
import { enderecoDeReuniao, enderecoDoCliente } from '../../core/dominios';
import { chamarFuncao } from '../../data/functions';

/** Monta a URL de salto e troca de host. Chamada de dentro de um `useEffect`
 *  em `RaizDoPainel` - nunca durante o render, é efeito colateral puro. */
export async function saltarParaSubdominio(
  slug: string,
  destino: string,
  /**
   * Para qual porta do cliente: o painel (`cima.yardsuite.com.br`, o padrão)
   * ou o suitemail (`cima.mail.yardsuite.com.br`). O `/_sso` existe nos dois
   * hosts - é rota estática, vence o `/*` do suitemail -, então a sessão
   * chega inteira de qualquer lado. Antes a troca para a porta de mail
   * (a porta do suitemail em `/mail`, `app/Router.tsx`) era um `window.location.href` cru, e
   * a pessoa chegava deslogada do outro lado.
   */
  origem: (slug: string) => string = enderecoDoCliente,
) {
  const { data } = await (supabase?.auth.getSession() ?? Promise.resolve({ data: { session: null } }));
  const sessao = data.session;

  // Sem sessão para levar, nada de fragment - só troca de host mesmo, a tela
  // de login do lado de lá resolve (é o mesmo caso de quem chega por link
  // direto no subdomínio).
  if (!sessao) {
    window.location.href = `${origem(slug)}${destino}`;
    return;
  }

  const fragmento = new URLSearchParams({
    at: sessao.access_token,
    rt: sessao.refresh_token,
    to: destino,
  }).toString();

  window.location.href = `${origem(slug)}/_sso#${fragmento}`;
}

/** A rota `/_sso`, do lado do subdomínio. */
export function ReceberDeSubdominio() {
  const navegar = useNavigate();

  useEffect(() => {
    let cancelado = false;
    const fragmento = window.location.hash.replace(/^#/, '');
    // Some da fragment da barra de endereço assim que lida - é o que impede
    // o token de sobreviver num "voltar" do navegador ou num print de tela
    // de quem está por perto. Só a fragment, sem tocar no caminho: quem
    // troca o caminho é o `navegar` abaixo, pelo React Router - trocar
    // `pathname` por fora dele (como esta função já fez antes) engana só a
    // barra de endereço, e o roteador continua sem saber que mudou nada.
    window.history.replaceState(null, '', window.location.pathname);
    aplicarSessao(fragmento).then((destino) => {
      if (!cancelado) navegar(destino, { replace: true });
    });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

async function aplicarSessao(fragmento: string): Promise<string> {
  const params = new URLSearchParams(fragmento);
  const at = params.get('at');
  const rt = params.get('rt');
  const to = params.get('to') || '/home';
  const destino = to.startsWith('/') ? to : '/home';

  // Sem par de tokens (ou sem cliente Supabase no build): não há sessão para
  // aplicar. A tela de login do lado de cá resolve - é o mesmo caso de quem
  // chega direto no subdomínio, sem ter passado pelo salto.
  if (!at || !rt || !supabase) return destino;

  // B1: espera a sessão existir de verdade do outro lado antes de navegar.
  // Antes o `setSession` era disparado sem `await` (`void supabase.auth
  // .setSession(...)`), e `navegar` rodava no mesmo tique: o `AuthProvider`
  // deste subdomínio já tinha resolvido `getSession()` como `null` e fixado
  // `unauthed`, então o destino "logado" caía na porta de entrada do
  // cliente, como se o login não tivesse acontecido. Às vezes "funcionava"
  // porque a corrida ia para o lado certo - era isso que fazia o defeito
  // parecer intermitente em vez de sempre quebrado.
  const { error } = await supabase.auth.setSession({ access_token: at, refresh_token: rt });
  // Sem sessão válida, não faz sentido mandar para um destino que exige
  // login - volta para a porta do cliente, com o erro na tela, nunca para um
  // destino logado que só ia bater de volta no `RouteGuard`.
  if (error) return '/?erro=sessao';
  return destino;
}

/* ================= A ponte para o Meet =================

   O link curto de reunião abre em `meet.yardsuite.com.br`, que é outra
   origem: a dona da reunião chegava lá sem sessão e entrava como convidada
   da própria chamada. A ponte leva a sessão, sem levar os tokens do painel:

     1. Em `meet.`, sem sessão, a sala chama `iniciarPonteParaOMeet(slug,
        sala)`: grava um nonce no sessionStorage DESTA aba e vem para
        `{slug}.yardsuite.com.br/_ponte-meet?sala=CODIGO&n=<sha256 do nonce>`.
     2. Aqui (`PonteParaOMeet`), com sessão, o painel pede à função
        `meet-ponte` um código curto, de uso único, que vale 60 s, amarrado à
        sala, ao usuário e ao hash do nonce, e volta para
        `meet.../reuniao/CODIGO#c=<código>`. Sem sessão, volta com
        `?convidado=1`, e a sala não tenta de novo.
     3. Na sala, `lerSessaoDaPonte(sala)` troca código + nonce por um magic
        link do mesmo usuário, gerado no servidor, e abre uma sessão PRÓPRIA
        do Meet (`verifyOtp`).

   Antes (até 25/10/2026) o passo 2 mandava `#at=..&rt=..` da sessão do
   painel: um XSS no Jitsi (mesma origem) lia o refresh token longo, e a sala
   aceitava qualquer fragment, então um link com os tokens do atacante logava
   a vítima na conta dele (login CSRF). Agora o refresh token do painel nunca
   sai do painel, e código sem o nonce desta aba não abre nada.
   Regras do servidor: `supabase/functions/_shared/ponte-meet.ts`.
*/

const CODIGO_DE_SALA = /^[a-z0-9][a-z0-9-]{2,63}$/i;
const HASH_DO_NONCE = /^[0-9a-f]{64}$/;
const CODIGO_DA_PONTE = /^[A-Za-z0-9_-]{32,64}$/;
const chaveDoNonce = (sala: string) => `ponte-meet:${sala.toLowerCase()}`;

async function sha256Hex(v: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(v));
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function novoNonce(): string {
  const b = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Passo 1, na sala do Meet sem sessão: grava o nonce e vai para a porta do dono. */
export async function iniciarPonteParaOMeet(slug: string, sala: string): Promise<void> {
  if (!CODIGO_DE_SALA.test(sala)) return;
  const nonce = novoNonce();
  try {
    sessionStorage.setItem(chaveDoNonce(sala), nonce);
  } catch {
    return; // sem sessionStorage não há como provar a volta: segue como convidada
  }
  const n = await sha256Hex(nonce);
  window.location.replace(`${enderecoDoCliente(slug)}/_ponte-meet?sala=${encodeURIComponent(sala)}&n=${n}`);
}

/** Passo 2: a rota `/_ponte-meet`, no host do cliente. */
export function PonteParaOMeet() {
  useEffect(() => {
    const busca = new URLSearchParams(window.location.search);
    const sala = busca.get('sala') ?? '';
    const n = busca.get('n') ?? '';
    const meet = enderecoDeReuniao();
    if (!CODIGO_DE_SALA.test(sala)) {
      window.location.replace(meet);
      return;
    }
    const comoConvidada = () => window.location.replace(`${meet}/reuniao/${sala}?convidado=1`);
    if (!HASH_DO_NONCE.test(n)) {
      comoConvidada();
      return;
    }
    void (supabase?.auth.getSession() ?? Promise.resolve({ data: { session: null } })).then(async ({ data }) => {
      if (!data.session) return comoConvidada();
      try {
        const r = await chamarFuncao<{ codigo?: string }>('meet-ponte', { acao: 'criar', sala, nonceHash: n });
        if (!r?.codigo || !CODIGO_DA_PONTE.test(r.codigo)) return comoConvidada();
        window.location.replace(`${meet}/reuniao/${sala}#c=${r.codigo}`);
      } catch {
        comoConvidada();
      }
    });
  }, []);
  return (
    <div className="state" aria-busy="true">
      <p className="muted">Conferindo sua conta…</p>
    </div>
  );
}

/** O que a sala recebeu na fragment, já validado. Separado para o teste. */
export function lerFragmentoDaPonte(hash: string): { codigo: string } | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  // O formato antigo (`#at=&rt=`) não vale mais: é exatamente o login CSRF.
  if (params.has('at') || params.has('rt')) return null;
  const codigo = params.get('c') ?? '';
  return CODIGO_DA_PONTE.test(codigo) ? { codigo } : null;
}

/**
 * Passo 3, na sala do Meet: apaga a fragment da barra ANTES de tudo, confere
 * que esta aba começou a ponte (nonce no sessionStorage, usado uma vez) e
 * troca o código por uma sessão própria do Meet. Devolve se abriu sessão.
 */
export async function lerSessaoDaPonte(sala: string): Promise<boolean> {
  const hash = window.location.hash;
  if (!hash) return false;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  const recebido = lerFragmentoDaPonte(hash);
  if (!recebido || !supabase || !CODIGO_DE_SALA.test(sala)) return false;

  let nonce: string | null = null;
  try {
    nonce = sessionStorage.getItem(chaveDoNonce(sala));
    sessionStorage.removeItem(chaveDoNonce(sala));
  } catch {
    /* sem sessionStorage: sem nonce */
  }
  // Sem nonce, esta aba não pediu a ponte: é link de outra pessoa.
  if (!nonce) return false;

  try {
    const r = await chamarFuncao<{ tokenHash?: string }>('meet-ponte', { acao: 'trocar', codigo: recebido.codigo, sala, nonce });
    if (!r?.tokenHash) return false;
    const { error } = await supabase.auth.verifyOtp({ token_hash: r.tokenHash, type: 'email' });
    return !error;
  } catch {
    return false;
  }
}
